import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    HEARING_OUTCOME_OPTIONS,
    HearingOutcome,
    type Hearing,
    type SelectOption,
    type UpdateHearingPayload,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

interface AddHearingValue {
    scheduledAt: string;
    heldAt: string;
    outcome: string;
    outcomeStatus: HearingOutcome | '';
    description: string;
}

// Gerencia as audiências de um caso: lista, adiciona, edita (marcar realizada = heldAt +
// outcome) e remove. Fetch próprio pelo id do caso. Mesmo padrão do useLegalCaseDeadlines.
// "Realizada" deriva da presença de heldAt (não há enum de status obrigatório) — a edição é
// parcial (updateHearing).
export default function useLegalCaseHearings(caseId: string | null, initialHearings?: Hearing[]) {
    const [hearings, setHearings] = useState<Hearing[]>(initialHearings || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [outcomeOptions, setOutcomeOptions] = useState<SelectOption[]>(HEARING_OUTCOME_OPTIONS);
    const seededCaseRef = useRef<string | null>(null);
    // Geração de request: guarda contra resposta obsoleta. O fetch do mount pode chegar depois
    // de uma mutação e sobrescrever a lista com o snapshot antigo. Cada fetch captura a geração
    // ao iniciar e só aplica o resultado se ainda for a corrente (o próprio ++ invalida os em voo).
    const fetchGenRef = useRef(0);

    const fetchHearings = useCallback(async () => {
        if (!caseId) return;
        const generation = ++fetchGenRef.current;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getHearings(caseId);
            if (fetchGenRef.current === generation) setHearings(result.items || []);
        } catch {
            // silencioso — a seção já mostra as audiências que vieram no page-view
        } finally {
            if (fetchGenRef.current === generation) setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setHearings(initialHearings || []);
        setErrorFields([]);
        // Semeia com o page-view (paint imediato) e re-busca do servidor. O re-fetch cobre a
        // remontagem da seção (ao alternar de aba): sem ele, voltaria ao `initialHearings` do
        // page-view — carregado antes de qualquer inclusão — e o registro recém-criado
        // "sumiria" até recarregar a página. initialHearings fora das deps de propósito.
        fetchHearings();
        // initialHearings fora das deps de propósito (referência nova a cada render).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    useEffect(() => {
        const loadOptions = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.hearingOutcomes?.length) setOutcomeOptions(opts.hearingOutcomes);
            } catch {
                // fallback local já preenche
            }
        };
        loadOptions();
    }, []);

    const clearErrors = () => setErrorFields([]);

    const addHearing = async (data: AddHearingValue): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addHearing(caseId, {
                scheduledAt: data.scheduledAt,
                heldAt: data.heldAt || undefined,
                outcome: data.outcome || undefined,
                outcomeStatus: data.outcomeStatus || undefined,
                description: data.description || undefined,
            });
            await fetchHearings();
            toast.success('Audiência registrada');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso não encontrado');
                return false;
            }
            toast.error(e?.message || 'Erro ao registrar audiência');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const updateHearing = async (
        hearingId: string,
        data: UpdateHearingPayload,
    ): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        try {
            await recoveryApi.updateHearing(caseId, hearingId, data);
            await fetchHearings();
            toast.success('Audiência atualizada');
            return true;
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar audiência');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removeHearing = async (hearingId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeHearing(caseId, hearingId);
            await fetchHearings();
            toast.success('Audiência removida');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover audiência');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        hearings, isLoading, isSaving, errorFields, outcomeOptions,
        addHearing, updateHearing, removeHearing, clearErrors,
    };
}

export type { AddHearingValue };
